/**
 * Raccord entre le thème et l'appli Joy Wishlist (Avada), 20.10.2026.
 * Ne modifie rien du code de Joy : ce fichier écoute la page et rattrape deux comportements du thème.
 *
 * 1. Cœur de l'en-tête. Joy insère une seule fois <joy-wishlist-icon variant="header"> dans le bouton
 *    du panier. Après un ajout au panier, le thème re-rend la section d'en-tête par morph
 *    (component-cart-items.js → morphSection) : le morph retire tout nœud absent du HTML du serveur,
 *    donc le cœur, et Joy ne le recrée pas. On remet le même nœud (écouteurs et état de Joy intacts)
 *    à sa place, avant <cart-icon>, dans la même tâche que le morph : pas de clignotement.
 *
 * 2. Ajout au panier depuis la liste. Comme la page contient <cart-items-component>, Joy annonce
 *    l'ajout par l'événement standard « shopify:cart:lines-update » (promesse : { cart, detail }),
 *    qu'Horizon 3.4 n'écoute pas. Pour un ajout venu de Joy (detail.source === 'joy-wishlist'),
 *    on émet l'événement du thème (cart:update) : bulle du compteur et tiroir se mettent à jour.
 */
import { CartUpdateEvent } from '@theme/events';

const ICONE = 'joy-wishlist-icon';
const ICONE_EN_TETE = `${ICONE}[variant="header"]`;

/* 1. Cœur de l'en-tête */

// Garde-fou : jamais plus de 10 remises par seconde (si quelque chose retirait le cœur en boucle).
let remises = 0;
let debutFenetre = 0;

/**
 * @param {Element} icone - le cœur retiré
 * @param {Node} ancienParent - le nœud d'où il a été retiré
 */
function remettre(icone, ancienParent) {
  if (icone.isConnected) return; // déplacé, pas retiré
  if (document.querySelector(ICONE_EN_TETE)) return; // un autre cœur est déjà là (Joy l'a recréé)

  const maintenant = Date.now();
  if (maintenant - debutFenetre > 1000) {
    debutFenetre = maintenant;
    remises = 0;
  }
  if (++remises > 10) return;

  // Place d'origine : le même bouton, s'il est toujours dans la page, sinon le panier de l'en-tête.
  let ancre = null;
  if (ancienParent instanceof Element && ancienParent.isConnected) {
    ancre = Array.from(ancienParent.children).find((enfant) => enfant.localName === 'cart-icon') ?? null;
  }
  ancre ??= document.querySelector('header-actions cart-icon');
  if (!ancre?.parentElement) return;

  ancre.parentElement.insertBefore(icone, ancre);
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const noeud of mutation.removedNodes) {
      if (!(noeud instanceof Element)) continue;
      const icones = noeud.localName === ICONE ? [noeud] : Array.from(noeud.getElementsByTagName(ICONE));
      for (const icone of icones) {
        if (icone.getAttribute('variant') === 'header') remettre(icone, mutation.target);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

/* 2. Ajout au panier depuis la liste d'envies */

document.addEventListener('shopify:cart:lines-update', async (event) => {
  const promesse = /** @type {any} */ (event).promise;
  if (!promesse || typeof promesse.then !== 'function') return;

  let resultat;
  try {
    resultat = await promesse;
  } catch {
    return;
  }
  if (resultat?.detail?.source !== 'joy-wishlist' || resultat.detail.didError) return;

  const itemCount = resultat.cart?.totalQuantity ?? resultat.detail.itemCount;
  if (typeof itemCount !== 'number') return;

  // Sans « sections » : cart-items-component re-rend lui-même sa section (tiroir) ; la bulle prend le total.
  document.dispatchEvent(new CartUpdateEvent({}, 'joy-wishlist', { source: 'joy-wishlist', itemCount }));
});
